export const ENV_STORAGE_KEY = 'Ten99PolicyEnvironment';

// Which contractor profile the portal is currently showing. Sent on every
// request as the Ten99Policy-Organization header, so it decides which of a
// multi-agency contractor's profiles they are acting on.
export const ORG_STORAGE_KEY = 'Ten99PolicyOrganization';

/**
 * Forget the selected organization.
 *
 * Call this on sign-out. The value used to survive sign-out, so the next person
 * (or the next sign-in from a different invitation) inherited a selection that
 * may not belong to them — landing a contractor with two agency profiles on
 * whichever one was open last regardless of the link they just used.
 */
export const clearSelectedOrganization = () => {
  if (typeof localStorage !== 'undefined') {
    localStorage.removeItem(ORG_STORAGE_KEY);
  }
};

/**
 * Decide which organization the portal should show, in priority order:
 *
 *   1. `?org=` from a contractor-scoped magic link — the profile they asked
 *      for. The stored value survives sign-out and previous sessions, so
 *      without this the link they just clicked loses to whatever they happened
 *      to select last, and a card update lands on the wrong agency.
 *   2. A stored selection that is still accessible.
 *   3. First accessible organization.
 *
 * Every candidate is checked against `accessibleOrgIds` first. A value that is
 * not in that list — access revoked, a leftover from another account on a
 * shared browser, or a hand-edited link — is DISCARDED rather than trusted,
 * whichever source it came from. Sending one makes the backend resolve no
 * contractor and fail every query with CONTRACTOR_ACCESS_REQUIRED: a blank
 * portal on every reload, unfixable without clearing site data by hand.
 *
 * That guard is also what lets the one-shot recovery reload work. Recovery
 * clears the stored selection but cannot rewrite the URL, so an unresolvable
 * `?org=` is still on it when the page comes back — and is rejected here
 * rather than reapplied, letting the fallback to an accessible profile happen.
 */
export const resolveOrganizationId = ({
  linkedOrgId,
  storedOrgId,
  accessibleOrgIds,
  firstOrgId,
}: {
  linkedOrgId: string | null;
  storedOrgId: string | null;
  accessibleOrgIds: string[];
  firstOrgId: string;
}): string => {
  const isAccessible = (orgId: string | null): orgId is string =>
    !!orgId && accessibleOrgIds.includes(orgId);

  if (isAccessible(linkedOrgId)) {
    return linkedOrgId;
  }
  if (isAccessible(storedOrgId)) {
    return storedOrgId;
  }
  return firstOrgId;
};

/**
 * Pick the contractor-organization entry a page should display, given what is
 * stored.
 *
 * Pages that read contractor data have to agree with the header Apollo sends,
 * because that header is what the backend resolves mutations against. Falling
 * back to the first profile when a stored id is simply *missing* from the list
 * breaks that agreement: requests keep carrying the stored id while the page
 * describes a different agency.
 *
 * So the fallback applies only when nothing is stored at all. A stored id that
 * is absent from the list returns undefined, and the caller is expected to show
 * a loading state — the selection is stale and the Apollo error link is already
 * clearing it, so the condition is short-lived and not something to render
 * around.
 */
export const selectContractorOrganization = <
  T extends { organization?: { publicId?: string | null } | null },
>(
  contractorOrgs: readonly T[] | null | undefined,
  storedOrgId: string | null
): T | undefined => {
  if (!contractorOrgs) return undefined;
  if (!storedOrgId) return contractorOrgs[0];
  return contractorOrgs.find(
    (co) => co.organization?.publicId === storedOrgId
  );
};

/**
 * Whether a stored organization is stale — present in storage, absent from the
 * profiles the user actually has.
 *
 * A caller showing a loading state for this is betting that something will
 * resolve it: the switcher re-resolves, or the Apollo error link clears the
 * selection and reloads. That bet is only good when the user HAS profiles.
 *
 * With an empty list nothing ever clears the stored id — the switcher returns
 * early before it writes storage, and a successful empty response never reaches
 * the CONTRACTOR_ACCESS_REQUIRED path — so treating it as "still resolving"
 * produces a spinner with no end. An empty list is not a stale selection; it is
 * a user with no profiles, and the page should render whatever it renders for
 * that.
 */
export const hasStaleOrganizationSelection = (
  contractorOrgs:
    | readonly { organization?: { publicId?: string | null } | null }[]
    | null
    | undefined,
  storedOrgId: string | null
): boolean => {
  if (!storedOrgId) return false;
  if (!contractorOrgs || contractorOrgs.length === 0) return false;
  return !selectContractorOrganization(contractorOrgs, storedOrgId);
};

// Records that we have already reloaded once to recover from a selected
// organization the backend would not resolve a contractor for.
//
// sessionStorage rather than a module variable because the reload it guards
// tears the module down, and per-tab because the recovery is per-tab.
const ORG_RECOVERY_KEY = 'Ten99PolicyOrgRecoveryReload';

/**
 * True once an automatic org-recovery reload has happened in this tab.
 *
 * Deliberately NOT reset when a query succeeds: the organization list loading
 * fine says nothing about whether the *selected* org resolves, so clearing on
 * any success would let a fallback that also fails reload forever — the exact
 * loop this guard exists to stop. An explicit switch is the one thing that
 * earns a fresh attempt, because the user chose a different profile.
 */
export const hasAttemptedOrgRecovery = () => {
  if (typeof sessionStorage === 'undefined') return false;
  return sessionStorage.getItem(ORG_RECOVERY_KEY) === '1';
};

export const markOrgRecoveryAttempted = () => {
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.setItem(ORG_RECOVERY_KEY, '1');
  }
};

export const clearOrgRecoveryAttempt = () => {
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.removeItem(ORG_RECOVERY_KEY);
  }
};

/**
 * Forget the profile-scoped state of the current contractor session: the
 * selected organization and the one-shot recovery budget that goes with it.
 *
 * Call this on sign-out, voluntary or forced — signing in again in the same tab
 * is a new session and earns a fresh recovery attempt. Clearing the selection
 * alone would leave the flag set, so a tab that had already spent its one
 * reload would give the next person no automatic recovery at all: straight to
 * the broken empty state the reload exists to rescue them from.
 *
 * Kept separate from clearSelectedOrganization because the recovery path itself
 * clears the selection and then marks an attempt. Folding the flag reset into
 * that function would make the loop guard depend on the order of two calls.
 */
export const clearContractorSessionState = () => {
  clearSelectedOrganization();
  clearOrgRecoveryAttempt();
};

// Session revocation state management
let isSessionRevoked = false;
let isSessionRevocationInProgress = false;

export const isSessionRevokedFlag = () => isSessionRevoked;
export const isSessionRevocationInProgressFlag = () => isSessionRevocationInProgress;
export const setSessionRevoked = (value: boolean) => {
  isSessionRevoked = value;
};
export const setSessionRevocationInProgress = (value: boolean) => {
  isSessionRevocationInProgress = value;
};
export const resetSessionRevokedFlags = () => {
  isSessionRevoked = false;
  isSessionRevocationInProgress = false;
};

export const getStorageItem = (key: string, defaultValue = '') => {
  if (typeof localStorage === 'undefined') {
    return defaultValue;
  }
  return localStorage.getItem(key) || defaultValue;
};

export const setEnvironment = (env: 'production' | 'sandbox') => {
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(ENV_STORAGE_KEY, env);
  }
};

export const getEnvironment = () => {
  return getStorageItem(
    ENV_STORAGE_KEY,
    process.env.NEXT_PUBLIC_ENVIRONMENT || 'production'
  );
};

export const getApiUrl = () => {
  return process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000';
};

export const getGraphqlUrl = () => {
  return process.env.NEXT_PUBLIC_GRAPHQL_BACKEND_URL || `${getApiUrl()}/graphql`;
};
